import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Permissions } from '@barahpay/shared';
import type { PermissionName } from '@barahpay/shared';
import type { ReactNode } from 'react';
import { AdminLayout } from './layout/AdminLayout';
import { RequireAuth, RequirePermission } from './auth/RequirePermission';
import { useAuth } from './auth/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { BarahLoader } from './components/BarahLoader';

/*
 * Screens load on demand. Twenty pages in one bundle would make the first paint pay for parts of the
 * portal a given role may never open, and most operators live on two or three screens.
 */
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const PaymentsPage = lazy(() => import('./pages/payments/PaymentsPage').then((m) => ({ default: m.PaymentsPage })));
const PaymentDetailPage = lazy(() =>
  import('./pages/payments/PaymentDetailPage').then((m) => ({ default: m.PaymentDetailPage })),
);
const InvestigationsPage = lazy(() =>
  import('./pages/payments/InvestigationsPage').then((m) => ({ default: m.InvestigationsPage })),
);
const PaymentLinksPage = lazy(() =>
  import('./pages/PaymentLinksPage').then((m) => ({ default: m.PaymentLinksPage })),
);
const RefundsPage = lazy(() => import('./pages/RefundsPage').then((m) => ({ default: m.RefundsPage })));
const DisputesPage = lazy(() => import('./pages/DisputesPage').then((m) => ({ default: m.DisputesPage })));
const SettlementsPage = lazy(() => import('./pages/SettlementsPage').then((m) => ({ default: m.SettlementsPage })));
const ReconciliationPage = lazy(() =>
  import('./pages/ReconciliationPage').then((m) => ({ default: m.ReconciliationPage })),
);
const CustomersPage = lazy(() => import('./pages/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() =>
  import('./pages/CustomersPage').then((m) => ({ default: m.CustomerDetailPage })),
);
const SubscriptionsPage = lazy(() =>
  import('./pages/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage })),
);
const BillingPage = lazy(() => import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })));
const PlansPage = lazy(() => import('./pages/PlansPage').then((m) => ({ default: m.PlansPage })));
const ProductsPage = lazy(() => import('./pages/ProductsPage').then((m) => ({ default: m.ProductsPage })));
const ProvidersPage = lazy(() => import('./pages/ProvidersPage').then((m) => ({ default: m.ProvidersPage })));
const WebhooksPage = lazy(() => import('./pages/WebhooksPage').then((m) => ({ default: m.WebhooksPage })));
const EventsPage = lazy(() => import('./pages/EventsPage').then((m) => ({ default: m.EventsPage })));
const SystemHealthPage = lazy(() =>
  import('./pages/SystemHealthPage').then((m) => ({ default: m.SystemHealthPage })),
);
const ReportsPage = lazy(() => import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const PaymentsPeriodReportPage = lazy(() =>
  import('./pages/reports/PaymentsPeriodReportPage').then((m) => ({ default: m.PaymentsPeriodReportPage })),
);
const PaymentProofReportPage = lazy(() =>
  import('./pages/reports/PaymentProofReportPage').then((m) => ({ default: m.PaymentProofReportPage })),
);
const HyperPayReconciliationPage = lazy(() =>
  import('./pages/reports/HyperPayReconciliationPage').then((m) => ({ default: m.HyperPayReconciliationPage })),
);
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((m) => ({ default: m.RolesPage })));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));

/** Wraps a screen in its permission gate and lazy-load boundary, keeping the route table declarative. */
function Gated({ permissions, children }: { permissions: PermissionName[]; children: ReactNode }) {
  return (
    <RequirePermission permissions={permissions}>
      <Suspense fallback={<PageSpinner />}>{children}</Suspense>
    </RequirePermission>
  );
}

function PageSpinner() {
  return <BarahLoader />;
}

/** A signed-in user never sees the sign-in page again until they sign out. */
function LoginRoute() {
  const { user, restoring } = useAuth();

  if (!restoring && user) {
    return <Navigate to="/" replace />;
  }

  return <LoginPage />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />

      <Route
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route
          index
          element={
            <Gated permissions={[Permissions.paymentsView]}>
              <DashboardPage />
            </Gated>
          }
        />

        <Route
          path="payments"
          element={
            <Gated permissions={[Permissions.paymentsView]}>
              <PaymentsPage />
            </Gated>
          }
        />
        <Route
          path="payments/:paymentNumber"
          element={
            <Gated permissions={[Permissions.paymentsView]}>
              <PaymentDetailPage />
            </Gated>
          }
        />
        <Route
          path="investigations"
          element={
            <Gated permissions={[Permissions.paymentsInvestigate, Permissions.paymentsView]}>
              <InvestigationsPage />
            </Gated>
          }
        />
        <Route
          path="payment-links"
          element={
            <Gated permissions={[Permissions.paymentLinksView]}>
              <PaymentLinksPage />
            </Gated>
          }
        />
        <Route
          path="refunds"
          element={
            <Gated permissions={[Permissions.refundsView]}>
              <RefundsPage />
            </Gated>
          }
        />
        <Route
          path="disputes"
          element={
            <Gated permissions={[Permissions.disputesView]}>
              <DisputesPage />
            </Gated>
          }
        />
        <Route
          path="disputes/:disputeNumber"
          element={
            <Gated permissions={[Permissions.disputesView]}>
              <DisputesPage />
            </Gated>
          }
        />
        <Route
          path="settlements"
          element={
            <Gated permissions={[Permissions.settlementsView]}>
              <SettlementsPage />
            </Gated>
          }
        />
        <Route
          path="settlements/:settlementNumber"
          element={
            <Gated permissions={[Permissions.settlementsView]}>
              <SettlementsPage />
            </Gated>
          }
        />
        <Route
          path="reconciliation"
          element={
            <Gated permissions={[Permissions.reconciliationView]}>
              <ReconciliationPage />
            </Gated>
          }
        />

        <Route
          path="customers"
          element={
            <Gated permissions={[Permissions.customersView]}>
              <CustomersPage />
            </Gated>
          }
        />
        <Route
          path="customers/:customerNumber"
          element={
            <Gated permissions={[Permissions.customersView]}>
              <CustomerDetailPage />
            </Gated>
          }
        />
        <Route
          path="subscriptions"
          element={
            <Gated permissions={[Permissions.subscriptionsView]}>
              <SubscriptionsPage />
            </Gated>
          }
        />
        <Route
          path="billing"
          element={
            <Gated permissions={[Permissions.billingView]}>
              <BillingPage />
            </Gated>
          }
        />

        <Route
          path="plans"
          element={
            <Gated permissions={[Permissions.plansView]}>
              <PlansPage />
            </Gated>
          }
        />
        <Route
          path="products"
          element={
            <Gated permissions={[Permissions.productsView]}>
              <ProductsPage />
            </Gated>
          }
        />

        <Route
          path="providers"
          element={
            <Gated permissions={[Permissions.providersView]}>
              <ProvidersPage />
            </Gated>
          }
        />
        <Route
          path="webhooks"
          element={
            <Gated permissions={[Permissions.webhooksView]}>
              <WebhooksPage />
            </Gated>
          }
        />
        <Route
          path="events"
          element={
            <Gated permissions={[Permissions.eventsView]}>
              <EventsPage />
            </Gated>
          }
        />
        <Route
          path="system-health"
          element={
            <Gated permissions={[Permissions.systemHealthView]}>
              <SystemHealthPage />
            </Gated>
          }
        />

        <Route
          path="reports"
          element={
            <Gated permissions={[Permissions.reportsView]}>
              <ReportsPage />
            </Gated>
          }
        />
        <Route
          path="reports/payments"
          element={
            <Gated permissions={[Permissions.reportsView]}>
              <PaymentsPeriodReportPage />
            </Gated>
          }
        />
        <Route
          path="reports/proof"
          element={
            <Gated permissions={[Permissions.reportsView]}>
              <PaymentProofReportPage />
            </Gated>
          }
        />
        <Route
          path="reports/reconciliation"
          element={
            <Gated permissions={[Permissions.reportsView]}>
              <HyperPayReconciliationPage />
            </Gated>
          }
        />
        <Route
          path="users"
          element={
            <Gated permissions={[Permissions.usersView]}>
              <UsersPage />
            </Gated>
          }
        />
        <Route
          path="roles"
          element={
            <Gated permissions={[Permissions.rolesView]}>
              <RolesPage />
            </Gated>
          }
        />
        <Route
          path="audit-logs"
          element={
            <Gated permissions={[Permissions.auditView]}>
              <AuditLogsPage />
            </Gated>
          }
        />
        <Route
          path="settings"
          element={
            <Gated permissions={[Permissions.settingsView]}>
              <SettingsPage />
            </Gated>
          }
        />

        {/* The profile page needs no permission: it is always the signed-in user's own record. */}
        <Route
          path="profile"
          element={
            <Suspense fallback={<PageSpinner />}>
              <ProfilePage />
            </Suspense>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
